import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  ApiError,
  attachmentCreateBodySchema,
  attachmentListSchema,
  attachmentPresignBodySchema,
  attachmentPresignSchema,
  attachmentRenameBodySchema,
  attachmentSchema,
  commentAttachmentCreateBodySchema,
  commentAttachmentSchema,
  type Attachment,
  type AttachmentCreateBody,
  type AttachmentList,
  type AttachmentPresign,
  type AttachmentPresignBody,
  type AttachmentRenameBody,
  type BoardContents,
  type CardDetail,
  type CommentAttachment,
  type CommentAttachmentCreateBody,
  type CommentStream,
} from '@molt/shared';

import { del, emptyResponseSchema, get, patch, post } from './api';
import { setBoardContentsQueries } from './cross-board-move';
import { queryKeys } from './query-keys';

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function getCardAttachments(cardId: string): Promise<AttachmentList> {
  return get(`/cards/${encodeURIComponent(cardId)}/attachments`, attachmentListSchema);
}

export function presignAttachmentUpload(
  cardId: string,
  body: AttachmentPresignBody,
): Promise<AttachmentPresign> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/attachments/presign`,
    attachmentPresignSchema,
    attachmentPresignBodySchema.parse(body),
  );
}

/**
 * `t` is required and first, mirroring `formatAttachmentSize` below: this is not
 * a React component, so it must never call `useInterfaceLanguage()` — with no
 * provider mounted the hook resolves against `fallbackCatalogue` and renders
 * English successfully, forever. Required and first also makes `tsc` enumerate
 * every caller instead of letting one site render English forever.
 */
export async function putAttachmentBytes(
  t: AttachmentTranslate,
  presign: AttachmentPresign,
  file: Blob,
): Promise<void> {
  let response: Response;

  try {
    response = await fetch(presign.url, {
      method: 'PUT',
      headers: presign.headers,
      body: file,
    });
  } catch (cause) {
    throw new ApiError('internal', t('primitives.attachment.upload.bytesFailed'), {
      network: [cause instanceof Error ? cause.message : String(cause)],
    });
  }

  if (!response.ok) {
    throw new ApiError(
      'internal',
      t('primitives.attachment.upload.refused', { status: response.status }),
    );
  }
}

export function createCardAttachment(
  cardId: string,
  body: AttachmentCreateBody,
): Promise<Attachment> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/attachments`,
    attachmentSchema,
    attachmentCreateBodySchema.parse(body),
  );
}

export function renameAttachment(
  attachmentId: string,
  body: AttachmentRenameBody,
): Promise<Attachment> {
  return patch(
    `/attachments/${encodeURIComponent(attachmentId)}/display-name`,
    attachmentSchema,
    attachmentRenameBodySchema.parse(body),
  );
}

export function deleteAttachment(attachmentId: string): Promise<void> {
  return del(
    `/attachments/${encodeURIComponent(attachmentId)}`,
    emptyResponseSchema,
  ).then(() => undefined);
}

/** Ordinary links allow browser viewing; Download controls explicitly opt in. */
export function attachmentDownloadPath(
  attachmentId: string,
  disposition: 'inline' | 'attachment' = 'inline',
): string {
  const path = `/api/attachments/${encodeURIComponent(attachmentId)}/download`;
  return disposition === 'attachment' ? `${path}?disposition=attachment` : path;
}

export function createCommentAttachment(
  commentId: string,
  body: CommentAttachmentCreateBody,
): Promise<CommentAttachment> {
  return post(
    `/comments/${encodeURIComponent(commentId)}/attachments`,
    commentAttachmentSchema,
    commentAttachmentCreateBodySchema.parse(body),
  );
}

export function cardAttachmentsQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.attachments(cardId),
    queryFn: () => getCardAttachments(cardId),
  });
}

// ---------------------------------------------------------------------------
// Variables
// ---------------------------------------------------------------------------

export interface CreateCardAttachmentVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly body: AttachmentCreateBody;
}

export interface RenameAttachmentVariables {
  readonly boardId?: string | undefined;
  readonly cardId: string;
  readonly attachmentId: string;
  readonly body?: AttachmentRenameBody | undefined;
  readonly displayName?: string | undefined;
}

export interface DeleteAttachmentVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly attachmentId: string;
}

export interface CreateCommentAttachmentVariables {
  readonly commentId: string;
  readonly cardId: string;
  readonly boardId: string;
  readonly body: CommentAttachmentCreateBody;
}

// ---------------------------------------------------------------------------
// Pure cache updaters
// ---------------------------------------------------------------------------

/**
 * Mirrors `CommentTranslate` (`lib/comments.ts:37-41`) so this module stays
 * decoupled from the interface-language context — a non-component module takes
 * a translator as a parameter rather than importing the React context.
 */
export type AttachmentTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

/**
 * `t` is required and first: five production call sites, so a missed one fails
 * `tsc` rather than rendering English forever.
 *
 * The arithmetic is deliberately unchanged and there is no `Intl.NumberFormat`
 * and no `locale` parameter — grouping would turn `1023 MB` into `1,023 MB`,
 * and which locale to pass is still an open question. `{size}` receives the
 * pre-formatted string this function already produced. The decimal separator
 * therefore stays `.` in every locale.
 */
export function formatAttachmentSize(
  t: AttachmentTranslate,
  sizeBytes: number | null,
): string {
  if (sizeBytes === null) return t('primitives.attachment.size.unknown');
  if (sizeBytes < 1024) return t('primitives.attachment.size.bytes', { size: sizeBytes });

  const units = [
    'primitives.attachment.size.kilobytes',
    'primitives.attachment.size.megabytes',
    'primitives.attachment.size.gigabytes',
  ] as const;
  let value = sizeBytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const formatted = value >= 10 ? Math.round(value).toString() : value.toFixed(1);
  // `noUncheckedIndexedAccess` is on, so `units[unitIndex]` is
  // `string | undefined`; the old template literal hid that and would have
  // rendered "undefined". The fallback must be a literal — `units.at(-1)` and
  // `units[units.length - 1]` do not narrow either. The loop bound keeps
  // `unitIndex` in `0..2`, so it is unreachable at runtime.
  const unitKey = units[unitIndex] ?? 'primitives.attachment.size.gigabytes';
  return t(unitKey, { size: formatted });
}

export function isPreviewableAttachment(attachment: Attachment): boolean {
  if (attachment.kind !== 'file') return false;

  const extension = attachment.displayName.split('.').at(-1)?.toLowerCase();
  return (
    extension !== undefined &&
    [
      'apng',
      'avif',
      'bmp',
      'csv',
      'gif',
      'jpeg',
      'jpg',
      'json',
      'md',
      'pdf',
      'png',
      'svg',
      'txt',
      'webp',
    ].includes(extension)
  );
}

export function replaceAttachmentInList(
  list: AttachmentList | undefined,
  attachment: Attachment,
): AttachmentList | undefined {
  if (list === undefined) return undefined;
  const existingIndex = list.findIndex((candidate) => candidate.id === attachment.id);
  if (existingIndex === -1) return [attachment, ...list];

  return list.map((candidate, index) =>
    index === existingIndex ? attachment : candidate,
  );
}

export function removeAttachmentFromList(
  list: AttachmentList | undefined,
  attachmentId: string,
): AttachmentList | undefined {
  if (list === undefined) return undefined;
  return list.filter((attachment) => attachment.id !== attachmentId);
}

export function removeAttachmentFromCardDetail(
  detail: CardDetail | undefined,
  attachmentId: string,
): CardDetail | undefined {
  if (detail === undefined) return undefined;
  return {
    ...detail,
    attachments: detail.attachments.filter(
      (attachment) => attachment.id !== attachmentId,
    ),
    cover:
      detail.cover?.source === 'attachment' && detail.cover.attachmentId === attachmentId
        ? null
        : detail.cover,
  };
}

export function upsertAttachmentInCardDetail(
  detail: CardDetail | undefined,
  attachment: Attachment,
): CardDetail | undefined {
  if (detail === undefined) return undefined;
  return {
    ...detail,
    attachments: replaceAttachmentInList(detail.attachments, attachment) ?? [],
  };
}

export function updateBoardContentsAttachmentCount(
  contents: BoardContents | undefined,
  cardId: string,
  delta: 1 | -1,
  removedAttachmentId?: string,
): BoardContents | undefined {
  if (contents === undefined) return undefined;

  return {
    ...contents,
    lists: contents.lists.map((list) => ({
      ...list,
      cards: list.cards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              attachmentCount: Math.max(0, card.attachmentCount + delta),
              cover:
                removedAttachmentId !== undefined &&
                card.cover?.source === 'attachment' &&
                card.cover.attachmentId === removedAttachmentId
                  ? null
                  : card.cover,
            }
          : card,
      ),
    })),
  };
}

export function addCommentAttachmentToStream(
  stream: CommentStream | undefined,
  commentId: string,
  attachment: CommentAttachment,
): CommentStream | undefined {
  if (stream === undefined) return undefined;
  return {
    ...stream,
    comments: stream.comments.map((comment) =>
      comment.id === commentId
        ? { ...comment, attachments: [...comment.attachments, attachment] }
        : comment,
    ),
  };
}

export function replaceAttachmentInCommentStream(
  stream: CommentStream | undefined,
  attachment: Attachment,
): CommentStream | undefined {
  if (stream === undefined) return undefined;
  return {
    ...stream,
    comments: stream.comments.map((comment) => ({
      ...comment,
      attachments: comment.attachments.map((commentAttachment) =>
        commentAttachment.kind === 'file' &&
        commentAttachment.attachment?.id === attachment.id
          ? { ...commentAttachment, attachment }
          : commentAttachment,
      ),
    })),
  };
}

export function removeAttachmentFromCommentStream(
  stream: CommentStream | undefined,
  attachmentId: string,
): CommentStream | undefined {
  if (stream === undefined) return undefined;
  return {
    ...stream,
    comments: stream.comments.map((comment) => ({
      ...comment,
      attachments: comment.attachments.map((commentAttachment) =>
        commentAttachment.kind === 'file' &&
        commentAttachment.attachment?.id === attachmentId
          ? { ...commentAttachment, attachment: null }
          : commentAttachment,
      ),
    })),
  };
}

function writeAttachmentSuccess(
  queryClient: QueryClient,
  cardId: string,
  boardId: string,
  attachment: Attachment,
): void {
  queryClient.setQueryData<AttachmentList>(queryKeys.cards.attachments(cardId), (list) =>
    replaceAttachmentInList(list, attachment),
  );
  queryClient.setQueryData<CardDetail>(queryKeys.cards.detail(cardId), (detail) =>
    upsertAttachmentInCardDetail(detail, attachment),
  );
  setBoardContentsQueries(queryClient, boardId, (contents) =>
    updateBoardContentsAttachmentCount(contents, cardId, 1),
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useCreateCardAttachment(): UseMutationResult<
  Attachment,
  unknown,
  CreateCardAttachmentVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => createCardAttachment(variables.cardId, variables.body),
    onSuccess: (attachment, variables) => {
      writeAttachmentSuccess(
        queryClient,
        variables.cardId,
        variables.boardId,
        attachment,
      );
    },
  });
}

export function useRenameAttachment(): UseMutationResult<
  Attachment,
  unknown,
  RenameAttachmentVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) =>
      renameAttachment(
        variables.attachmentId,
        variables.body ?? { displayName: variables.displayName ?? '' },
      ),
    onSuccess: (attachment, variables) => {
      queryClient.setQueryData<AttachmentList>(
        queryKeys.cards.attachments(variables.cardId),
        (list) => replaceAttachmentInList(list, attachment),
      );
      queryClient.setQueryData<CardDetail>(
        queryKeys.cards.detail(variables.cardId),
        (detail) => upsertAttachmentInCardDetail(detail, attachment),
      );
      queryClient.setQueryData<CommentStream>(
        queryKeys.cards.comments(variables.cardId),
        (stream) => replaceAttachmentInCommentStream(stream, attachment),
      );
    },
  });
}

export function useDeleteAttachment(): UseMutationResult<
  void,
  unknown,
  DeleteAttachmentVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => deleteAttachment(variables.attachmentId),
    onSuccess: (_deleted, variables) => {
      queryClient.setQueryData<AttachmentList>(
        queryKeys.cards.attachments(variables.cardId),
        (list) => removeAttachmentFromList(list, variables.attachmentId),
      );
      queryClient.setQueryData<CardDetail>(
        queryKeys.cards.detail(variables.cardId),
        (detail) => removeAttachmentFromCardDetail(detail, variables.attachmentId),
      );
      queryClient.setQueryData<CommentStream>(
        queryKeys.cards.comments(variables.cardId),
        (stream) => removeAttachmentFromCommentStream(stream, variables.attachmentId),
      );
      setBoardContentsQueries(queryClient, variables.boardId, (contents) =>
        updateBoardContentsAttachmentCount(
          contents,
          variables.cardId,
          -1,
          variables.attachmentId,
        ),
      );
    },
  });
}

export function useCreateCommentAttachment(): UseMutationResult<
  CommentAttachment,
  unknown,
  CreateCommentAttachmentVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) =>
      createCommentAttachment(variables.commentId, variables.body),
    onSuccess: (commentAttachment, variables) => {
      queryClient.setQueryData<CommentStream>(
        queryKeys.cards.comments(variables.cardId),
        (stream) =>
          addCommentAttachmentToStream(stream, variables.commentId, commentAttachment),
      );

      if (commentAttachment.kind === 'file' && commentAttachment.attachment !== null) {
        writeAttachmentSuccess(
          queryClient,
          variables.cardId,
          variables.boardId,
          commentAttachment.attachment,
        );
      }
    },
  });
}
